import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { toast } from 'react-toastify'

import { useGuestCardChannelDefaults } from '../../../hooks/Outreach/useGuestCardChannelDefaults'
import { useGuestCards } from '../../../hooks/Outreach/useGuestCards'
import { useLeads } from '../../../hooks/Outreach/useLeads'
import { GuestCardFormRequest } from '../../../hooks/Outreach/useGuestCardForm'
import { useAptConfig } from '../../../hooks/useAptConfig'
import { usePremiums } from '../../../hooks/usePremiums'
import { GuestCardFormFields } from '../../Outbound/Leads/ProspectsTab/GuestCardFormFields'

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            borderRadius: theme.spacing(2),
            maxWidth: 720,
            width: '100%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
        },
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    dialogContent: {
        padding: theme.spacing(3),
        overflowY: 'auto',
    },
    subtitle: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
}))

interface AddWalkInLeadDialogProps {
    open: boolean
    onClose: () => void
    seasonId: number
    apartmentId: number
    onSuccess?: () => void
}

export const AddWalkInLeadDialog: React.FC<AddWalkInLeadDialogProps> = ({
    open,
    onClose,
    seasonId,
    apartmentId,
    onSuccess,
}) => {
    const classes = useStyles()
    const { guestCards, isLoadingGuestCards } = useGuestCards()
    const {
        defaults,
        isLoading: isLoadingDefaults,
    } = useGuestCardChannelDefaults(seasonId)
    const { convertToLeadMutation } = useLeads({
        apartmentId,
        enabled: false,
    })
    const { unitConfigList } = useAptConfig({ unitConfigList: true })
    const { premiums } = usePremiums()

    const isLoadingCard = isLoadingGuestCards || isLoadingDefaults
    const walkInCardId = defaults.walk_in
    const selectedGuestCard = useMemo(() => {
        if (walkInCardId == null || !guestCards?.length) return null
        return guestCards.find((card) => card.id === walkInCardId) ?? null
    }, [guestCards, walkInCardId])

    const guestCardFormData = useMemo(() => {
        if (!selectedGuestCard) return null
        const unitConfigs = (
            unitConfigList.filter((config) => config.shows_on_schedule) || []
        ).map((config) => ({
            ...config,
            bed_count:
                (config as { bed_count?: number | null }).bed_count ?? null,
            bath_count:
                (config as { bath_count?: number | null }).bath_count ?? null,
        }))
        return {
            guest_card: selectedGuestCard,
            name: null,
            email: null,
            phone: null,
            unit_configs: unitConfigs,
            premiums: premiums.filter((premium) => premium.is_active),
            apartment_name: '',
        }
    }, [selectedGuestCard, unitConfigList, premiums])

    const [formKey, setFormKey] = useState(0)

    useEffect(() => {
        if (!open) return
        setFormKey((k) => k + 1)
        convertToLeadMutation.reset()
    }, [open])

    const handleSubmit = (formData: GuestCardFormRequest) => {
        if (!selectedGuestCard) {
            toast.error('Walk-in guest card is not configured on this season.')
            return
        }
        convertToLeadMutation.mutate(
            {
                ...formData,
                guest_card_id: selectedGuestCard.id,
                walk_in: true,
                leasing_season_id: seasonId,
            },
            {
                onSuccess: () => {
                    onSuccess?.()
                    onClose()
                },
            },
        )
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={classes.dialog}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle disableTypography className={classes.dialogTitle}>
                <Box>
                    <Typography variant="h6">Add walk-in lead</Typography>
                    <Typography variant="body2" className={classes.subtitle}>
                        Capture information from someone who visited the
                        property in person.
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                {isLoadingCard ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : !guestCardFormData ? (
                    <Typography className={classes.subtitle}>
                        Walk-in guest card is not configured on this season. Set
                        it on the Guest Card pack in Leasing Season Config.
                    </Typography>
                ) : (
                    <GuestCardFormFields
                        key={formKey}
                        guestCardFormData={guestCardFormData}
                        prospect={null}
                        onSubmit={handleSubmit}
                        isLoading={convertToLeadMutation.isLoading}
                        submitLabel="Save Lead"
                        loadingLabel="Saving..."
                    />
                )}
            </DialogContent>
        </Dialog>
    )
}
